import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LabelToId } from 'src/app/common/input-utils'
import {
  ServicePlanFieldError,
  ServicePlanStepComponent,
} from 'src/app/models/service-plan/service-plan-field-error'
import { ServicePlanStateService } from 'src/app/services/service-plan/service-plan-state.service'

// Step 4 — Baseline Annual Support Package (story e8-4). A single required
// confirmation field over otherwise read-only package info + a hardcoded fee
// line-item table (see feeAmountDisplay doc comment below). The confirmation
// is prefilled from payload like every other field, so it's blank only on the first
// ever display — see ngOnInit. Required-field/isLiveValid mechanics copied from
// PlanTypeStepComponent's established pattern, not NdisPlanStepComponent's/
// NdisClassificationStepComponent's no-required-fields shape.
@Component({
  selector: 'app-service-plan-baseline-package-step',
  templateUrl: './baseline-package-step.component.html',
  styleUrls: ['./baseline-package-step.component.scss'],
  standalone: false,
})
export class BaselinePackageStepComponent
  implements OnInit, OnDestroy, ServicePlanStepComponent
{
  // The Annual Support Package fee is a single fixed constant — there is no API
  // exposing it to the frontend (see story e8-4 Dev Notes). The backend's own copy of
  // this exact figure lives in ServicePlanFeeCalculator.cs (AnnualSupportPackageFee),
  // used only by Epic 6's PDF renderer — this is a known, accepted duplication, not a
  // shared constant across the Angular/.NET boundary. Displayed here as a 4-line
  // breakdown (Lee's line-item table, sourced from the FY26 fee schedule) that sums to
  // the same figure, plus a bottom total row — not a single line, and not a change to
  // the Total Fee calculation itself, which still only ever sees the one total.
  // Fixed display string for the total-row amount, not formatted via CurrencyPipe, so
  // the rendered figure stays deterministic regardless of the app's configured locale.
  // Same reasoning applies to each line item's own amountDisplay below.
  readonly feeAmountDisplay = '$2,618.88'

  readonly feeLineItemKeys: ReadonlyArray<{
    labelKey: string
    descriptionKey: string
    amountDisplay: string
  }> = [
    {
      labelKey: 'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_PROGRESS_LABEL',
      descriptionKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_PROGRESS_DESCRIPTION',
      amountDisplay: '$969.96',
    },
    {
      labelKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_GOAL_SETTING_LABEL',
      descriptionKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_GOAL_SETTING_DESCRIPTION',
      amountDisplay: '$290.99',
    },
    {
      labelKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_PLANNING_DELIVERY_LABEL',
      descriptionKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_PLANNING_DELIVERY_DESCRIPTION',
      amountDisplay: '$1,163.94',
    },
    {
      labelKey: 'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_AUDIOLOGY_LABEL',
      descriptionKey:
        'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_ITEM_AUDIOLOGY_DESCRIPTION',
      amountDisplay: '$193.99',
    },
  ]

  form!: FormGroup<{
    confirmed: FormControl<boolean>
  }>

  packageNameLabel = ''
  packageDescription = ''
  feeLineItems: {
    label: string
    description: string
    amountDisplay: string
  }[] = []
  feeTotalLabel = ''
  confirmationLabel = ''
  confirmationControlId = ''

  private subscription?: Subscription

  constructor(
    private state: ServicePlanStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.packageNameLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.BASELINE_PACKAGE.PACKAGE_NAME',
    )
    this.packageDescription = this.translate.instant(
      'SERVICE_PLAN.STEP.BASELINE_PACKAGE.PACKAGE_DESCRIPTION',
    )
    this.feeLineItems = this.feeLineItemKeys.map((item) => ({
      label: this.translate.instant(item.labelKey),
      description: this.translate.instant(item.descriptionKey),
      amountDisplay: item.amountDisplay,
    }))
    this.feeTotalLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.BASELINE_PACKAGE.FEE_TOTAL_LABEL',
    )
    this.confirmationLabel = this.translate.instant(
      'SERVICE_PLAN.STEP.BASELINE_PACKAGE.CONFIRMATION_LABEL',
    )
    this.confirmationControlId = LabelToId(this.confirmationLabel)

    // Prefilled from payload.baselinePackageConfirmed, same as every other step field —
    // blank on the very first display (a fresh payload defaults this to false), but once
    // the clinician ticks it, navigating forward and back (Continue/Back, or the rail)
    // must not force them to re-tick it on every pass through (Lee's explicit decision).
    const payload = this.state.getPayload()
    this.form = new FormGroup({
      confirmed: new FormControl(payload.baselinePackageConfirmed, {
        nonNullable: true,
        validators: Validators.requiredTrue,
      }),
    })

    this.subscription = this.form.valueChanges.subscribe((value) => {
      this.state.updatePayload({
        baselinePackageConfirmed: value.confirmed ?? false,
      })
    })
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe()
  }

  // ServicePlanStepComponent contract — the shell's Continue button reads this
  // directly (non-mutating), unlike isValid() below which calls markAllAsTouched()
  // on click. Matches PlanTypeStepComponent's required-field precedent (AC2's
  // "Resolved" note — epics.md's disabled-Continue wins over EXPERIENCE.md's draft
  // click-validation-only wording for this field).
  get isLiveValid(): boolean {
    return this.form?.valid ?? false
  }

  isValid(): boolean {
    this.form.markAllAsTouched()
    return this.form.valid
  }

  getValidationErrors(): ServicePlanFieldError[] {
    if (this.form.controls.confirmed.invalid) {
      return [
        {
          controlId: this.confirmationControlId,
          messageKey:
            'SERVICE_PLAN.VALIDATION.BASELINE_PACKAGE_CONFIRMATION_REQUIRED',
        },
      ]
    }
    return []
  }
}
